import React, {
    ChangeEventHandler,
    FocusEvent,
    Dispatch,
    forwardRef,
    InputHTMLAttributes,
    memo,
    useCallback,
    useState,
    KeyboardEventHandler
} from "react"
import {useLegacyRef} from "../../../hooks/legacy-ref"
import {KeyboardTuple} from "../../../hooks/keyboard"
import iconShow from "../../../assets/images/icons/icon-show.png"
import iconHide from "../../../assets/images/icons/icon-hide.png"

import "./index.scss"
import {onClickHandler} from "../../../utils/onclick"

type SettingTextareaComponentProps = {
    label: string
    value: string
    setValue: (newValue: string) => void
    setActiveInput: Dispatch<KeyboardTuple>
    tempValue?: string
}


export const SettingTextareaComponent = memo(
    forwardRef<HTMLTextAreaElement, InputHTMLAttributes<HTMLTextAreaElement> & SettingTextareaComponentProps>(
        function SettingTextareaComponent(props, ref) {
            const {
                label,
                value,
                setValue,
                setActiveInput,
                tempValue,
                type,
                onFocus: onFocusProp,
                onBlur: onBlurProp,
                ...restProps
            } = props
            const inputRef = useLegacyRef(ref)

            // noinspection JSUnusedLocalSymbols
            const onFocus = useCallback(
                (event: FocusEvent<HTMLTextAreaElement>) => {
                    if (typeof onFocusProp === "function") {
                        onFocusProp(event)
                    }

                    let valueLast = value
                    if (tempValue != null) {
                        if (value === tempValue) {
                            valueLast = ""
                            setValue("")
                        }
                    }

                    if (typeof setActiveInput === "function") {
                        setActiveInput([inputRef, valueLast, setValue])
                    }
                },
                [onFocusProp, value, tempValue, setActiveInput, inputRef, setValue]
            )

            const onBlur = useCallback(
                (event: FocusEvent<HTMLTextAreaElement>) => {
                    if (typeof onBlurProp === "function") {
                        onBlurProp(event)
                    }

                    if (tempValue != null && value === "") {
                        setValue(tempValue)
                    }
                },
                [onBlurProp, setValue, tempValue, value]
            )

            const onChange: ChangeEventHandler<HTMLTextAreaElement> = useCallback(
                e => {
                    setValue(e.target.value)
                },
                [setValue]
            )

            const onKeyPress: KeyboardEventHandler<HTMLTextAreaElement> = useCallback(
                e => {
                    if (type === "number-password") {
                        if (!/\d/.test(String.fromCharCode(e.which))) {
                            e.preventDefault()
                        }
                    }
                },
                [type]
            )

            return (
                <div className="line settings-input" style={{height:'auto'}}>
                    <div className="left">
                        <span>{label}</span>
                    </div>
                    <div className="right">
                        <textarea
                            maxLength={150}
                            onChange={onChange}
                            className="settings-textarea"
                            value={value}
                            ref={inputRef}
                            onKeyPress={onKeyPress}
                            onFocus={onFocus}
                            onBlur={onBlur}
                            {...restProps}
                        />
                    </div>
                </div>
            )
        }
    )
)
